/*
 * Gorgias chat integration for the AUTOID theme. Four jobs:
 *
 * 1. Mirror the chat window's open state (iframe#chat-window only, not the campaign teaser) onto
 *    <body class="gorgias-chat-open">, so base.css keeps the widget below the consent banner and sticky bars
 *    until the shopper opens the chat.
 * 2. Mirror the sticky add to cart bar's stuck state onto <body class="sticky-atc-stuck">.
 * 3. Skin the chat launcher (the "CONTACT US" pill). Gorgias draws it from three stacked gradient and blur
 *    layers and exposes no setting for them. The launcher lives in a same-origin srcdoc iframe
 *    (#gorgias-chat-container iframe#chat-button), so the theme can add a style element inside it. The skin
 *    replaces the gradient with a flat white pill, a hairline border, a calmer shadow, no grow on hover and
 *    the Barbados focus ring, the same recipe as the design system's white pill.
 * 4. Follow the colourway (dark and halloween): the launcher, the campaign teaser and the open chat window
 *    are restyled live, on the colour switch's autoid:colourway event as well as on load.
 *
 * The launcher stays hidden until it is skinned (rule in autoid-design-system.css) so the gradient never
 * flashes. If the skin cannot be applied (Gorgias changes its markup, or the iframe stops being same-origin)
 * the launcher is revealed unskinned after SKIN_GIVE_UP_MS and keeps working.
 */
(function () {
  var RETRY_MS = 250;
  var MAX_TRIES = 40;
  var SKIN_GIVE_UP_MS = 5000;
  var SKIN_STYLE_ID = 'autoid-gorgias-skin';
  var BUTTON_ID = 'gorgias-chat-messenger-button';

  /* Lives inside the launcher iframe, so the theme's custom properties are not available: values are the
     design system's (Titanium #F5F7F9, ink-90 #E9EDF1, Midnight #121417, Barbados #006EFF, ease-standard). */
  var SKIN_CSS =
    '#' + BUTTON_ID + ' > div:empty { display: none !important; }' +
    '#' + BUTTON_ID + ' {' +
    ' background: #fff !important;' +
    ' box-shadow: 0 0 0 1px #E9EDF1, 0 2px 6px rgba(18, 20, 23, 0.14) !important;' +
    ' transition: background-color 120ms cubic-bezier(0.2, 0, 0, 1) !important;' +
    ' transform: none !important;' +
    ' }' +
    '#' + BUTTON_ID + ':hover { background: #F5F7F9 !important; transform: none !important; }' +
    '#' + BUTTON_ID + ':active { background: #E9EDF1 !important; transform: none !important; }' +
    '#' + BUTTON_ID + ':focus-visible { outline: 2px solid #006EFF !important; outline-offset: 2px !important; }';

  /* Dark and halloween. Gorgias exposes no dark mode, and the frames are same-origin, so a second style element
     per frame carries the dark colours; it is removed again in light, which is why the light skin above is
     left exactly as it was. Values are the colourway tokens (card #1C2026, soft well #242A31, hairline #2A3038,
     link blue #6E9BF2, Ember #FF8A3D for the halloween focus ring).
       - chat-button: the same flat pill on the card colour, blue text and bubble lifted to the link blue.
       - chat-campaigns: the teaser card (the "Have a question about this product?" message).
       - chat-window: the open chat is a large app with literal colours throughout, so it is inverted as a
         whole (near-black, not pure black) and photos are turned back. The AUTOID logo is the one image served
         from smooch_inside, a black mark that the inversion is what turns white. */
  var SCHEME_STYLE_ID = 'autoid-gorgias-scheme';

  function darkCss(frameId) {
    var ring = document.documentElement.dataset.colourway === 'halloween' ? '#FF8A3D' : '#006EFF';

    if (frameId === 'chat-button') {
      var btn = 'html #' + BUTTON_ID;
      return btn + ' { background: #1C2026 !important; box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.14), 0 2px 6px rgba(0, 0, 0, 0.4) !important; }' +
        btn + ':hover { background: #242A31 !important; }' +
        btn + ':active { background: #2A3038 !important; }' +
        btn + ':focus-visible { outline-color: ' + ring + ' !important; }' +
        btn + ' span { color: #6E9BF2 !important; }' +
        btn + ' svg path { fill: #6E9BF2 !important; }' +
        btn + ' svg ellipse { fill: #1C2026 !important; }';
    }

    if (frameId === 'chat-campaigns') {
      var card = '[data-testid="campaigns-container"] [role="dialog"]';
      return card + ' { background: #1C2026 !important; color: #F5F7F9 !important; box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.14), 4px 4px 16px rgba(0, 0, 0, 0.4) !important; }' +
        card + ' h3 { color: #F5F7F9 !important; }' +
        card + ' [data-testid="dismiss-campaign-btn"], ' + card + ' [data-testid="dismiss-campaign-btn"] div { color: #F5F7F9 !important; }' +
        card + ' [data-testid="dismiss-campaign-btn"]:hover, ' + card + ' [data-testid="dismiss-campaign-btn"]:focus-visible { background: rgba(255, 255, 255, 0.12) !important; }' +
        card + ' .reply-area { background: #242A31 !important; color: #AAB2BD !important; }' +
        card + ':focus { box-shadow: 0 0 0 3px ' + ring + ', 0 3px 15px rgba(0, 0, 0, 0.4) !important; }';
    }

    if (frameId === 'chat-window') {
      return 'html { filter: invert(0.92) hue-rotate(180deg); }' +
        'img:not([src*="smooch_inside"]) { filter: invert(1) hue-rotate(180deg); }';
    }

    return '';
  }

  /* Adds, updates or removes the dark style element in every Gorgias frame that has a readable document. */
  function applyScheme(container) {
    var dark = document.documentElement.dataset.scheme === 'dark';

    Array.prototype.forEach.call(container.querySelectorAll('iframe'), function (frame) {
      if (!frame.hasAttribute('data-autoid-scheme-seen')) {
        frame.setAttribute('data-autoid-scheme-seen', '');
        frame.addEventListener('load', function () { applyScheme(container); });
      }

      try {
        var doc = frame.contentDocument;
        if (!doc || !doc.head) return;

        var css = dark ? darkCss(frame.id) : '';
        var style = doc.getElementById(SCHEME_STYLE_ID);

        if (!css) {
          if (style) style.remove();
        } else if (!style) {
          style = doc.createElement('style');
          style.id = SCHEME_STYLE_ID;
          style.textContent = css;
          doc.head.appendChild(style);
        } else if (style.textContent !== css) {
          style.textContent = css;
        }
      } catch (e) { /* cross-origin frame: nothing can be restyled */ }
    });
  }

  function setupChatState(tries) {
    var container = document.getElementById('gorgias-chat-container');
    if (!container) {
      if (tries < MAX_TRIES) setTimeout(function () { setupChatState(tries + 1); }, RETRY_MS);
      return;
    }

    var rafId = null;

    function check() {
      rafId = null;
      // Only the chat window counts as open. The campaign teaser (iframe#chat-campaigns, about 300px tall)
      // is not: counting it lifted the whole widget to Gorgias's own z-index, so the teaser covered the
      // consent banner, Add to cart and the vehicle picker.
      var chat = container.querySelector('iframe#chat-window');
      var isOpen = false;
      if (chat) {
        var style = window.getComputedStyle(chat);
        isOpen = style.display !== 'none' && style.visibility !== 'hidden' && chat.getBoundingClientRect().height > 150;
      }
      document.body.classList.toggle('gorgias-chat-open', isOpen);
      skinLauncher(container);
      applyScheme(container);
    }

    function scheduleCheck() {
      if (rafId === null) rafId = requestAnimationFrame(check);
    }

    check();
    // The colour switch changes the scheme without a reload; the frames are restyled to match.
    document.addEventListener('autoid:colourway', scheduleCheck);
    new MutationObserver(scheduleCheck).observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'width', 'height'],
    });
  }

  /* Adds the skin to the launcher iframe once its document has rendered the button, then reveals the launcher.
     Runs on every container mutation and on a short timer until it succeeds; harmless to repeat. */
  function skinLauncher(container) {
    var frame = container.querySelector('iframe#chat-button');
    if (!frame || frame.hasAttribute('data-autoid-skinned')) return;

    var seen = Number(frame.getAttribute('data-autoid-skin-seen'));
    if (!seen) {
      seen = Date.now();
      frame.setAttribute('data-autoid-skin-seen', String(seen));
      frame.addEventListener('load', function () { skinLauncher(container); });
    }

    var done = false;
    try {
      var doc = frame.contentDocument;
      if (doc && doc.head && doc.getElementById(BUTTON_ID)) {
        if (!doc.getElementById(SKIN_STYLE_ID)) {
          var style = doc.createElement('style');
          style.id = SKIN_STYLE_ID;
          style.textContent = SKIN_CSS;
          doc.head.appendChild(style);
        }
        done = true;
      }
    } catch (e) {
      done = true; // cross-origin frame: nothing can be skinned, reveal it as Gorgias draws it
    }

    if (done || Date.now() - seen > SKIN_GIVE_UP_MS) {
      frame.setAttribute('data-autoid-skinned', '');
    } else {
      setTimeout(function () { skinLauncher(container); }, 60);
    }
  }

  function watchStickyBar(tries) {
    var bar = document.querySelector('.sticky-add-to-cart__bar');
    if (!bar) {
      if (tries < 8) setTimeout(function () { watchStickyBar(tries + 1); }, RETRY_MS);
      return;
    }

    function sync() {
      var stuck = bar.getAttribute('data-stuck') === 'true';
      document.body.classList.toggle('sticky-atc-stuck', stuck);
    }

    sync();
    new MutationObserver(sync).observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
  }

  setupChatState(0);
  watchStickyBar(0);
})();
